import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@trovata/environments/environment';
import { StablecoinBalance, StablecoinBalanceHistoryPoint, StablecoinWallet } from '../../models/wallet.model';
import { StablecoinDepositInstruction, StablecoinDepositInstructionCreate } from '../../models/deposit-instruction.model';
import { StablecoinWorkflowPaymentType } from '../../models/workflow.model';
import { StablecoinApprovalDecisionValue } from '../../models/transfer.model';
import { StablecoinAccountFunding, StablecoinOnchainAccountLink } from '../../models/account-funding.model';

/**
 * Low-level HTTP service for stablecoin wallets (accounts, balances, funding).
 * State/components should use StablecoinWalletsFacadeService instead.
 */
@Injectable({
	providedIn: 'root',
})
export class StablecoinWalletsService {
	private readonly http: HttpClient = inject(HttpClient);
	private readonly baseUrl: string = environment.stablecoinAPI();

	getWallets(organizationId?: string | null): Observable<StablecoinWallet[]> {
		let params: HttpParams = new HttpParams();
		if (organizationId) {
			params = params.set('organization_id', organizationId);
		}
		return this.http.get<StablecoinWallet[]>(`${this.baseUrl}/accounts`, { params });
	}

	getWalletBalances(accountId: string): Observable<StablecoinBalance[]> {
		return this.http.get<StablecoinBalance[]>(`${this.baseUrl}/accounts/${accountId}/balances`);
	}

	getBalanceHistory(fromDate: string, toDate: string, accountId?: string | null): Observable<StablecoinBalanceHistoryPoint[]> {
		let params: HttpParams = new HttpParams().set('from_date', fromDate).set('to_date', toDate);
		if (accountId) {
			params = params.set('account_id', accountId);
		}
		return this.http.get<StablecoinBalanceHistoryPoint[]>(`${this.baseUrl}/reports/balances/history`, { params });
	}

	getDepositInstructions(accountId: string): Observable<StablecoinDepositInstruction[]> {
		const params: HttpParams = new HttpParams().set('account_id', accountId);
		return this.http.get<StablecoinDepositInstruction[]>(`${this.baseUrl}/deposit-instructions`, { params });
	}

	createDepositInstructions(body: StablecoinDepositInstructionCreate): Observable<StablecoinDepositInstruction> {
		return this.http.post<StablecoinDepositInstruction>(`${this.baseUrl}/deposit-instructions`, body);
	}

	/** Enable → pending_approval (dual control); disable → off immediately and cancels in-flight payments of that type. */
	setPaymentTypeEnabled(accountId: string, type: StablecoinWorkflowPaymentType, enabled: boolean): Observable<StablecoinWallet> {
		return this.http.put<StablecoinWallet>(`${this.baseUrl}/accounts/${accountId}/payment-types/${type}`, { enabled });
	}

	/** Approve (→ on) or reject (→ off) a pending enable request. The requester can't decide. */
	decidePaymentType(accountId: string, type: StablecoinWorkflowPaymentType, decision: StablecoinApprovalDecisionValue): Observable<StablecoinWallet> {
		return this.http.post<StablecoinWallet>(`${this.baseUrl}/accounts/${accountId}/payment-types/${type}/decide`, { decision });
	}

	/** Admin: sets (or clears, with null) the Coinbase account a Paxos account's on-chain payments send from. Audited; no dual control. */
	setOnchainAccount(accountId: string, onchainAccountId: string | null): Observable<StablecoinWallet> {
		const body: StablecoinOnchainAccountLink = { onchain_account_id: onchainAccountId };
		return this.http.put<StablecoinWallet>(`${this.baseUrl}/accounts/${accountId}/onchain-account`, body);
	}

	/** Available balance + deposit instructions of a Coinbase account (a linked Paxos account resolves to its Coinbase account). */
	getAccountFunding(accountId: string): Observable<StablecoinAccountFunding> {
		return this.http.get<StablecoinAccountFunding>(`${this.baseUrl}/accounts/${accountId}/funding`);
	}
}
